import { GameTrackOptions } from '@/model/tracking';
import { JackpotBetFeedsDataType } from '@/types/jackpotTypes';
import { StrikeGoldData } from '@/utils/strikeGold/types';

import { CategoryGameGridWithContentType } from './../../types/category/categoryTypes';
import {
  CloseStrategyEnum,
  GameCloseStrategies,
  GameLaunchStrategies,
  LaunchStrategyEnum,
  PortalVerticalGameTilePropTypes,
  Table,
} from './../../types/gameTypes';
import {
  JackpotsV2ResponseType,
  MultipleJackpotV2Interface,
} from './../../types/jackpotTypes';

export interface ControllersDataItem {
  components: [
    string,
    {
      type: string;
      properties: any;
    }
  ][];
}

type ActionType =
  | 'STATIC'
  | 'BASECAMP'
  | 'GAME_DETAILS'
  | 'GAME_LAUNCH_MODAL'
  | 'SEARCH';

export interface ControllersData {
  categories?: ControllersDataItem;
  recentlyPlayed?: ControllersDataItem;
  search?: ControllersDataItem;
  promotions?: ControllersDataItem;
  gameDetails?: ControllersDataItem;
  gameLauncher?: ControllersDataItem;
  searchBar?: ControllersDataItem;
  searchDesktopWrapper?: ControllersDataItem;
  bingoGames?: ControllersDataItem;
  dynamicRouteAction?: ControllersDataItem;
  videoModal?: ControllersDataItem;
}

interface TrackingData {
  page?: {
    actionType: ActionType;
    SiteLanguage: string | null;
    SiteSubSection: string | null;
    SiteSectionCategory: string | null;
    SiteSectionSubCategory: string | null;
    PageGameName: string | null;
  };
  sidebar?: {
    actionType: ActionType;
    SiteLanguage: string | null;
    SiteSubSection: string | null;
    SiteSectionCategory: string | null;
    SiteSectionSubCategory: string | null;
    PageGameName: string | null;
  };
  SiteSection: string;
  ProductContext: string | null;
}

export enum DynamicRouteActionTypesEnum {
  OPEN_DEPOSIT_MODAL = 'Open deposit modal',
  OPEN_REGISTRATION_MODAL = 'Open registration modal',
}

export type LogicContext = {
  appInitialized: boolean;
  mainLayoutPath: string;
  controllersData: ControllersData;
  navigationLinks: string[];
  currentScreenSize: 'small' | 'medium' | 'wide';
  initialRequest: boolean;
  restoreScroll: boolean;
  currentPagePath: string;
  pendingAccountEvents: object[];
  isAccountInfoAvailable: boolean;
  headerInitialized: boolean;
  rpFirstLoad: boolean;
  rpVisible: boolean;
  sessionResolved: boolean;
  segments?: string[] | null;
  gameLaunchStrategies: GameLaunchStrategies;
  gameCloseStrategies: GameCloseStrategies;
  trackingData: TrackingData;
  isErrorPage?: boolean;
  tealiumUserId?: string;
  gameToLaunch?: {
    triggeredByUser: boolean;
    path?: string;
  } & PortalVerticalGameTilePropTypes;
  updates: Array<{ type: string; data: any }>;
  deviceInfo: DeviceInfo;
  multiJackpots: Record<string, Record<string, MultipleJackpotV2Interface>>;
  currentJackpotsV2: Record<string, JackpotsV2ResponseType>;
  jackpotBetFeeds: JackpotBetFeedsDataType;
  strikeGoldData: StrikeGoldData;
  currentTablesIdList: string[];
  currentTables?: { [key: string]: Table };
  user: UserSessionInfo;
  userStatusForCurrentPage: boolean;
  loginModalState: {
    isOpened: boolean;
    isAwaitingLogin: boolean;
  };
  previousPageArray: {
    pagePath: string;
    isInSidebar: boolean;
    trackingData: TrackingData['page'];
  }[];
  seo: {
    page: PageSeo;
    sidebar?: PageSeo;
  };
  pageMainTitle: string;
  search: SearchInfo;
  phrasePendingAdditionToRecentPhrases?: string;
  recentPhrases: string[];
  delayedTrackingAvailable: boolean;
  isSidebarInLayout: boolean;
  isVideoEnabled: boolean;
  changePageAction: string;
  previousUserAccountNumber: string;
  inputConfig: {
    isIOS: boolean;
    isRenderedInIFrame: boolean;
  };
  personalizedBanners?: { selector: string; slug?: string }[];
  currentPersonalizedBanners?: { [key: string]: boolean };
  clickedOnSuggestedGame?: boolean;
  isLaunchedFromLauncher?: boolean;
  isFullScreen?: boolean;
  bingoGames?: PortalVerticalGameTilePropTypes[];
  isSwitchingGame?: boolean;
  isGameModalForMobileOpened?: boolean;
  shuffleGames: {
    status: number | null;
    availableList: PortalVerticalGameTilePropTypes[];
    drawnList: number[];
    drawnGame: PortalVerticalGameTilePropTypes | null;
  };
  routeDynamicAction?: DynamicRouteActionTypesEnum;
  searchBarComponent: { id: string };
  idlePopupComponent: {
    id: string;
    enabled?: boolean;
    checkboxEnabled?: boolean;
    bonusPermissionRequired?: boolean;
    isBonusPermission?: boolean;
    isLongPauseActive?: boolean;
    cookieTimestamp?: string;
    longPauseTimeInterval?: number;
    shortPauseTimeInterval?: number;
    idlePopupGame?: PortalVerticalGameTilePropTypes;
  };
  recommendedGames?: PortalVerticalGameTilePropTypes[];
  floatingIconComponent: { id: string };
  jackpotBetWidgetComponent: { id: string };
};

export interface PageSeo {
  title: string;
  description: string;
  image: string;
  canonicalLinks: {
    origin: 'same' | 'cross';
    url: string;
  }[];
  noIndex: boolean;
  noTranslate: boolean;
  structuredData: string;
}

export enum SearchSourceTypeEnum {
  SEARCH_INPUT = 'Search Input',
  RECENT_SEARCHES = 'Recent Searches',
  RECOMMENDED_FOR_YOU = 'Recommended for you',
  NULL = 'Null',
}

export type AuthTokenEvent = {
  type: string;
  data: {
    access_token?: string;
    expires_in?: number;
    refresh_token?: string;
    error?: string;
  };
};

export type SegmentsEvent = {
  type: string;
  data: {
    segments: string[] | null;
  };
};

export type GameLaunchEvent = {
  type: 'VIEW_CATEGORY_GAME_TILE_CLICK';
  payload: GameTrackOptions;
};

export type PromoClickEvent = {
  type: 'VIEW_CATEGORY_LINK_ITEM_CLICK';
  payload: {
    url?: string;
    path?: string;
    isBanner?: boolean;
    isPersonalizedBanner?: boolean;
  };
};

export type NavigationClickEvent = {
  type: 'VIEW_HEADER_MENU_ITEM_CLICK';
  payload: {
    path: string;
  };
};

export type SearchClickEvent = {
  type: 'VIEW_SEARCH_CLICK';
  payload: {
    value: string;
  };
};

export type SearchSubheaderClickEvent = {
  type: 'VIEW_SEARCH_SUBHEADER_ITEM_CLICK';
  payload: {
    index: number;
    type: string;
  };
};

export type FeatureModeType = 'enabled' | 'disabled' | 'hidden';
export type FeatureStateType = 'active' | 'inactive';

export type FeatureConfig = {
  mode: FeatureModeType;
  state: FeatureStateType;
};

export type UpdateRecentlyPlayedEvent = {
  type: '';
  data: {
    recentlyPlayedGamesList: PortalVerticalGameTilePropTypes[];
  };
};

export type UpdateBingoGamesEvent = {
  type: '';
  data: {
    bingoGames: PortalVerticalGameTilePropTypes[];
  };
};

export type UpdateJackpotsEvent = {
  type: '';
  data: {
    jackpotCode?: string;
    jackpots: {
      [key: string]: {
        state: 'active' | 'normal' | 'won';
      };
    };
    multiJackpots: Record<string, any>;
  };
};

export type UpdateTablesEvent = {
  type: '';
  data: {
    liveDealerTables: {
      [key: string]: Table;
    };
  };
};

export type IdlePopupRecommendedGamesEvent = {
  type: '';
  data: {
    status: number;
    recommendedGames: PortalVerticalGameTilePropTypes[];
  };
};

export type IdlePopupBonusPermissionEvent = {
  type: '';
  data: {
    status: number;
    data: { accountNumber: string; bonusBlocked: boolean };
  };
};

export type IdlePopupGameLaunchEvent = {
  type: 'VIEW_IDLE_POPUP_GAME_TILE_CLICK' | 'VIEW_IDLE_POPUP_PLAY_NOW_CLICK';
  payload: GameTrackOptions;
};

export type UpdateJackpotsV2Event = {
  type: '';
  data: {
    jackpots: Record<string, JackpotsV2ResponseType>;
  };
};

export type UpdateLaunchStrategiesEvent = {
  type: '';
  data: {
    gameLaunchStrategies: LaunchStrategyEnum[];
  };
};

export type UpdateCloseStrategiesEvent = {
  type: '';
  data: {
    gameCloseStrategies: CloseStrategyEnum[];
  };
};

export type UpdatePersonalizedBannersEvent = {
  type: '';
  payload: {
    banners: { container: string; rendered: boolean }[];
  };
};

export type DeviceInfo = {
  appVersion?: string;
  deviceType: 'mobile' | 'tablet' | 'desktop';
  isNative: boolean;
  nativeDeviceOs: 'ios' | 'android' | null;
  deviceOs: DeviceOs;
};

export type DeviceOs = DeviceOsValues | null;

export enum DeviceOsValues {
  Windows = 'windows',
  IOS = 'ios',
  OSX = 'os x',
  Android = 'android',
  Linux = 'linux',
}

export type UserSessionInfo = {
  loggedIn: boolean;
  firstName: string;
  userCurrency: string;
  userAccountNumber: string;
  userStatus: 'customer' | 'non-customer';
  userToken?: string;
  authToken?: {
    access_token?: string; // auth token internal/external flow
    refresh_token?: string; // auth token internal flow
    expires_in?: number; // value in seconds (auth token internal flow)
    initialSetup?: boolean; // auth token internal flow
  };
  recentlyPlayedCategory?: CategoryGameGridWithContentType;
  refreshTokenRetryNumber?: number;
  recentlyPlayedGamesList?: PortalVerticalGameTilePropTypes[];
  balance: number;
  currency: string;
};

export type RecentlyPlayedUpdated = {
  recentlyPlayedGamesList: PortalVerticalGameTilePropTypes[];
};

export type SearchInfo = {
  searchPhrase: string;
  sourceType: SearchSourceTypeEnum;
  grouppedItems: { [key: string]: Array<PortalVerticalGameTilePropTypes> };
  searchItems: Array<PortalVerticalGameTilePropTypes>;
  searchCategory: CategoryGameGridWithContentType;
  subheaderItems: Array<{ title: string; state: 'active' | 'inactive' }>;
  currentType: string;
  itemsNumber: number | null;
  attempts: number;
  userToken: string;
  queryID: string;
};

export type CallbackMode = 'event' | 'native';

export type UserInitData = {
  CustomerAccountNumber: string | null;
  CurrencyCode: string | null;
  UserStatus: 'Customer' | 'Non-Customer';
};

export interface Components {
  [key: string]: {
    type: string;
    properties: Record<string, unknown>;
    children: string[];
  };
}

export interface RenderManifest<Keys = Components> {
  components: Keys;
  children: (keyof Keys)[];
  personalizedBanners?: { container: string; slug?: string }[];
  gameLaunchStrategies: GameLaunchStrategies;
  gameCloseStrategies: GameCloseStrategies;
}

export interface Layout {
  root: string;
  components: Components;
  games: PortalVerticalGameTilePropTypes[];
  gameIds: string[];
}

export interface StaticPageDataComponents {
  Navigation: object;
  CookieBanner: object;
  Footer: object;
  Menu: object;
  BottomNavigation: object;
}

export interface Page {
  id: string;
  properties: {
    pageTitle: string;
    status?: 'FOUND' | 'NOT FOUND' | 'NOT_FOUND';
  };
  isStaticPage: boolean;
  liveDealerTables?: string[];
  gameLaunchStrategies: LaunchStrategyEnum[];
  gameCloseStrategies: CloseStrategyEnum[];
  trackingInfo?: { [key: string]: string | number | undefined };
  renderManifest: RenderManifest;
  staticPageComponentsData: StaticPageDataComponents;
  seo: {
    title: string;
  };
  games: PortalVerticalGameTilePropTypes[];
  gameIds: string[];
}

export type PartialEventData = {
  [key: string]: null | undefined | string | number | string[];
};

export type DeviceType = 'mobile' | 'tablet' | 'desktop';
